import { type FC, type ReactNode, useEffect, useId, useRef } from "react";

import { Box, Button, Column, Row, Spinner, Text } from "@hightouch/htui";

import { SkeletonItem } from "src/components/skeletons";

import { rowIndentPx } from "./usage-tree-geometry";
import { UsageTreeRow } from "./usage-tree-row";
import {
  countLoadedChildren,
  pendingChildRowCount,
  type UsageRow,
  type UsageTreeState,
  visibleRows,
} from "./usage-tree-state";

type Props = {
  state: UsageTreeState;
  filter: { types: ReadonlySet<string>; search: string };
  onToggle: (row: UsageRow) => void;
  onLoadMoreChildren: (row: UsageRow) => void;
  onLoadMorePage: (cursor: string) => void;
  isLoadingDisabled: boolean;
  emptyState: ReactNode;
  /** Grow to the height the container leaves instead of capping the tree. */
  fillHeight?: boolean;
};

export const UsageTree: FC<Props> = ({
  state,
  filter,
  onToggle,
  onLoadMoreChildren,
  onLoadMorePage,
  isLoadingDisabled,
  emptyState,
  fillHeight,
}) => {
  const { rows, forcedOpenKeys } = visibleRows(state, filter);
  const isFiltering = filter.types.size > 0 || filter.search.trim().length > 0;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rootSentinelRef = useRef<HTMLDivElement>(null);
  const rootCursor = state.rootCursor;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const sentinel = rootSentinelRef.current;
    if (
      !scrollContainer ||
      !sentinel ||
      typeof IntersectionObserver === "undefined" ||
      rootCursor === null ||
      state.isLoadingRootPage ||
      state.hasRootPageError ||
      isLoadingDisabled ||
      isFiltering
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onLoadMorePage(rootCursor);
        }
      },
      { root: scrollContainer, rootMargin: "160px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    isFiltering,
    isLoadingDisabled,
    onLoadMorePage,
    rootCursor,
    state.hasRootPageError,
    state.isLoadingRootPage,
  ]);

  if (rows.length === 0) {
    return <>{emptyState}</>;
  }

  const effectiveRows = rows.map((row) =>
    forcedOpenKeys.has(row.key) ? { ...row, isExpanded: true } : row,
  );
  const childrenByParent = new Map<string | null, UsageRow[]>();
  for (const row of effectiveRows) {
    const siblings = childrenByParent.get(row.parentKey) ?? [];
    siblings.push(row);
    childrenByParent.set(row.parentKey, siblings);
  }
  const rootRows = childrenByParent.get(null) ?? [];
  const loadedChildCounts = countLoadedChildren(state);

  return (
    <Column
      ref={scrollContainerRef}
      minHeight={0}
      flex={fillHeight ? 1 : undefined}
      maxHeight={fillHeight ? undefined : "lg"}
      overflowY="auto"
    >
      <Column as="ul" listStyleType="none" m={0} p={0}>
        {rootRows.map((row, index) => (
          <TreeBranch
            key={row.key}
            row={row}
            isLastSibling={index === rootRows.length - 1}
            ancestorConnectorContinuations={[]}
            childrenByParent={childrenByParent}
            loadedChildCounts={loadedChildCounts}
            onToggle={onToggle}
            onLoadMoreChildren={onLoadMoreChildren}
            isLoadingDisabled={isLoadingDisabled}
            isFiltering={isFiltering}
          />
        ))}
      </Column>

      {state.hasRootPageError && rootCursor !== null && (
        <Row role="alert" align="center" justify="center" gap={2} px={2} py={2}>
          <Text size="sm" color="text.danger">
            Couldn’t load more resources.
          </Text>
          <Button
            size="sm"
            variant="tertiary"
            isDisabled={isLoadingDisabled}
            onClick={() => onLoadMorePage(rootCursor)}
          >
            Retry
          </Button>
        </Row>
      )}

      {state.isLoadingRootPage && (
        <Row
          role="status"
          aria-label="Loading more resources"
          justify="center"
          gap={2}
          px={2}
          py={2}
        >
          <Spinner size="sm" />
          <Text size="sm" color="text.secondary">
            Loading more
          </Text>
        </Row>
      )}

      {rootCursor !== null &&
        !isFiltering &&
        !state.isLoadingRootPage &&
        !state.hasRootPageError &&
        !isLoadingDisabled && (
          <>
            <Box ref={rootSentinelRef} height="1px" aria-hidden />
            {typeof IntersectionObserver === "undefined" && (
              <Row px={2} py={2} justify="center">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onLoadMorePage(rootCursor)}
                >
                  Load more
                </Button>
              </Row>
            )}
          </>
        )}
    </Column>
  );
};

type TreeBranchProps = {
  row: UsageRow;
  isLastSibling: boolean;
  ancestorConnectorContinuations: readonly boolean[];
  childrenByParent: ReadonlyMap<string | null, UsageRow[]>;
  loadedChildCounts: ReadonlyMap<string, number>;
  onToggle: (row: UsageRow) => void;
  onLoadMoreChildren: (row: UsageRow) => void;
  isLoadingDisabled: boolean;
  isFiltering: boolean;
};

const TreeBranch: FC<TreeBranchProps> = ({
  row,
  isLastSibling,
  ancestorConnectorContinuations,
  childrenByParent,
  loadedChildCounts,
  onToggle,
  onLoadMoreChildren,
  isLoadingDisabled,
  isFiltering,
}) => {
  const childrenId = useId();
  const children = childrenByParent.get(row.key) ?? [];
  const isExpandable = row.directDependentCount > 0;
  // Filtering can alter how much space pending child rows take up, so we don't show them
  const showsPendingChildRows = row.isLoadingChildren && !isFiltering;

  return (
    <Column as="li" position="relative">
      <UsageTreeRow
        row={row}
        childrenId={childrenId}
        isLastSibling={isLastSibling}
        ancestorConnectorContinuations={ancestorConnectorContinuations}
        isLoadingDisabled={isLoadingDisabled}
        onToggle={onToggle}
      />

      {isExpandable && (
        <Column
          id={childrenId}
          hidden={!row.isExpanded}
          aria-busy={row.isLoadingChildren}
        >
          <Column as="ul" listStyleType="none" m={0} p={0}>
            {children.map((child, index) => (
              <TreeBranch
                key={child.key}
                row={child}
                isLastSibling={index === children.length - 1}
                ancestorConnectorContinuations={
                  row.depth === 0
                    ? ancestorConnectorContinuations
                    : [...ancestorConnectorContinuations, !isLastSibling]
                }
                childrenByParent={childrenByParent}
                loadedChildCounts={loadedChildCounts}
                onToggle={onToggle}
                onLoadMoreChildren={onLoadMoreChildren}
                isLoadingDisabled={isLoadingDisabled}
                isFiltering={isFiltering}
              />
            ))}
          </Column>

          {showsPendingChildRows && (
            <PendingChildRows
              depth={row.depth + 1}
              count={pendingChildRowCount(
                row,
                loadedChildCounts.get(row.key) ?? 0,
              )}
            />
          )}

          {row.hasChildrenError && (
            <Row
              role="alert"
              align="center"
              gap={2}
              pl={`${rowIndentPx(row.depth + 1)}px`}
              py={1}
            >
              <Text size="sm" color="text.danger">
                Couldn’t load what uses this.
              </Text>
              <Button
                size="sm"
                variant="tertiary"
                isDisabled={isLoadingDisabled}
                onClick={() => onLoadMoreChildren(row)}
              >
                Retry
              </Button>
            </Row>
          )}

          {row.childrenLoaded &&
            row.childCursor !== null &&
            !row.hasChildrenError && (
              <Row pl={`${rowIndentPx(row.depth + 1)}px`} py={1}>
                <Button
                  size="sm"
                  variant="tertiary"
                  isDisabled={isLoadingDisabled || row.isLoadingChildren}
                  onClick={() => onLoadMoreChildren(row)}
                >
                  Load more
                </Button>
              </Row>
            )}
        </Column>
      )}
    </Column>
  );
};

// Placeholders for expanded rows waiting for a response.
function PendingChildRows({ depth, count }: { depth: number; count: number }) {
  return (
    <Column aria-hidden>
      {new Array(count).fill(null).map((_, index) => (
        <Row
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows never reorder
          key={`pending-${index}`}
          align="center"
          px={2}
          py={1.5}
          pl={`${rowIndentPx(depth)}px`}
        >
          <SkeletonItem borderRadius="md" height={5} flex={1} />
        </Row>
      ))}
    </Column>
  );
}
